import { isAfter } from 'date-fns';

import { Reserve as ReserveV2 } from '@/types/Reserves';
import { Claim, Inzet, InzetOfClaim, WerklijnInline } from '@/types/werklijnenInline';
import { formatMomentMetWeekgrens } from '@/utils/dateUtils';

import { Reserve } from '../types';

export const isNietInzetbaarReserve = (reserve: Reserve) =>
  reserve.status === 'AANKOMEND' || reserve.status === 'ACHTERSTALLIG';

export const isAchterstalligeReserve = (reserve: Reserve) => reserve.status === 'ACHTERSTALLIG';

export const geefReserveTijd = (datum: Date) => formatMomentMetWeekgrens(datum, new Date());

export const geefReserveToelichting = (reserve: Reserve) => {
  if (!reserve.buitenDienstTijd) return null;

  if (reserve.status === 'AANKOMEND' || isAchterstalligeReserve(reserve)) {
    return `verwacht gereed: ${geefReserveTijd(reserve.buitenDienstTijd)}`;
  }
  return `vanaf ${geefReserveTijd(reserve.buitenDienstTijd)}`;
};

export const geefReserveToelichtingNotitieMarge = (
  heeftStatus: boolean,
  heeftOpmerking: boolean,
): 'geen' | 'status' | 'icoon' | 'beide' => {
  if (heeftStatus && heeftOpmerking) return 'beide';
  if (heeftStatus) return 'status';
  if (heeftOpmerking) return 'icoon';
  return 'geen';
};

/** Map a WerklijneInline to a Reserve object.
 *
 * @param werklijn
 * @returns
 */
export const derriveReserveFromWerklijn = (werklijn: WerklijnInline): Reserve => {
  const dienstregelpunt =
    (werklijn.inzettenEnClaims.at(-1) as Inzet)?.bewegingsdeeltraject?.aankomst?.dienstregelpunt ??
    (werklijn.inzettenEnClaims.at(-1) as Claim)?.aankomstDienstregelpunt ??
    werklijn.werklijnBegin?.dienstregelpunt;

  const buitenDienstTijd = getBuitenKomendeDienstTijd(werklijn.inzettenEnClaims);

  return {
    id: werklijn.id,
    eenheid: werklijn.eenheid.nummer,
    materieelType: `${werklijn.eenheid.materieelType.soort}-${werklijn.eenheid.materieelType.aanduiding}`,
    dienstregelpunt,
    buitenDienstTijd,
  };
};

/** Map een Reserve (reserves v2, GET /werklijnen/reserves) naar een Reserve object voor gebruik
 * in de Plank. Status AANKOMEND/ACHTERSTALLIG blijft behouden zodat de UI dit stel duidelijk kan
 * onderscheiden van een stel dat nu al reserve is.
 *
 * @param reserveV2
 * @returns
 */
export const derriveReserveFromReserveV2 = (reserveV2: ReserveV2): Reserve => ({
  id: reserveV2.werklijnId,
  eenheid: reserveV2.eenheid.nummer,
  materieelType: `${reserveV2.eenheid.materieelType.soort}-${reserveV2.eenheid.materieelType.aanduiding}`,
  dienstregelpunt: reserveV2.dienstregelpunt,
  buitenDienstTijd: reserveV2.beschikbaarVanaf ? new Date(reserveV2.beschikbaarVanaf) : null,
  status: reserveV2.status,
});

/** Geeft datum indien reserve buiten komende dienst (24uur in de toekomst liggen
 *
 * @param inzettenEnClaims
 * @returns date
 */
export const getBuitenKomendeDienstTijd = (inzettenEnClaims: InzetOfClaim[]) => {
  if (!inzettenEnClaims.length) {
    return null;
  }
  // Exclude claims met NSRRES, RES, '.' en OND (reserve claims)
  const filtered = inzettenEnClaims.filter(
    (value) => !('claimsoort' in value) || !['NSRRES', 'RES', '.', 'ONB'].includes(value.claimsoort),
  );
  const laatsteInzetOfClaim = filtered[filtered.length - 1];
  let laatsteTijd: string | null = null;

  if (laatsteInzetOfClaim && 'claimsoort' in laatsteInzetOfClaim) {
    laatsteTijd = laatsteInzetOfClaim.eindDatumTijd;
  } else if (laatsteInzetOfClaim && 'bewegingsdeeltraject' in laatsteInzetOfClaim) {
    laatsteTijd = laatsteInzetOfClaim.bewegingsdeeltraject.aankomst.uitvoeringsTijd;
  }

  return laatsteTijd && isAfter(new Date(laatsteTijd), new Date()) ? new Date(laatsteTijd) : null;
};

/** Map reserves to eenheid. Its used like this in order to map related data on common identifiers, ico reserve eenheid.
 *
 * @param reserves
 * @returns
 */
export const getReserveByStel = (reserves: Reserve[]): { [stel: string]: Reserve } =>
  reserves.reduce((obj, reserve) => {
    if (reserve.eenheid) obj[reserve.eenheid] = reserve;
    return obj;
  }, {});
